import { Application, Assets, ColorMatrixFilter, Graphics, Sprite, Texture } from 'pixi.js';
import { Spine, Physics } from '@esotericsoftware/spine-pixi-v8';
import { Viewport } from 'pixi-viewport';
import type { Layer, Transform, PhysicsConstraintPose } from '@spine-workspace/shared';
import { getLayerWorldTransform } from './transform-utils';

/** Data URL for a 64×64 "MISSING" placeholder texture drawn at module load time. */
const MISSING_TEXTURE_DATA_URL: string = (() => {
  if (typeof document === 'undefined') return '';
  try {
    const size = 64;
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    const ctx = c.getContext('2d');
    if (!ctx) return '';
    // Dark orange + checkerboard
    ctx.fillStyle = '#b33800';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#cc4400';
    for (let xi = 0; xi < 4; xi++) for (let yi = 0; yi < 4; yi++) {
      if ((xi + yi) % 2 === 0) ctx.fillRect(xi * 16, yi * 16, 16, 16);
    }
    // Diagonal cross
    ctx.strokeStyle = '#ff6600';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(4, 4); ctx.lineTo(60, 60);
    ctx.moveTo(60, 4); ctx.lineTo(4, 60);
    ctx.stroke();
    // Outer border
    ctx.strokeStyle = '#ff8800';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, size - 2, size - 2);
    // "MISSING" label
    ctx.fillStyle = '#ffe0cc';
    ctx.font = 'bold 8px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('MISSING', size / 2, size / 2);
    return c.toDataURL('image/png');
  } catch { return ''; }
})();

interface LoadedAsset {
  skeletonKey: string;
  atlasKey: string;
}

interface LoadedImage {
  textureKey: string;
}

export interface ViewportState {
  zoom: number;
  x: number;
  y: number;
}

export interface BonePosition {
  name: string;
  /** PixiJS stage-space X (add RULER_SIZE=20 for container CSS coords) */
  stageX: number;
  /** PixiJS stage-space Y (add RULER_SIZE=20 for container CSS coords) */
  stageY: number;
  parentName: string | null;
  /** Bone length in skeleton units (0 for root/attachment-point bones) */
  length: number;
}

export interface IkConstraintInfo {
  name: string;
  /** Ordered chain of bone names being constrained (tip-to-root) */
  bones: string[];
  /** Name of the IK target bone */
  targetBone: string;
  /** IK influence 0 (FK) → 1 (full IK) */
  mix: number;
  bendDirection: 1 | -1;
  softness: number;
  compress: boolean;
  stretch: boolean;
}

export interface IkTargetInfo {
  constraintName: string;
  /** Screen (SVG) X for handle rendering */
  stageX: number;
  stageY: number;
  /** Skeleton world coords of the target bone */
  worldX: number;
  worldY: number;
  /** Target bone's local coords (relative to its parent) — saved in IkPoseEntry */
  localX: number;
  localY: number;
}

export interface PhysicsConstraintInfo {
  name: string;
  /** Name of the constrained bone */
  bone: string;
  mix: number;
  inertia: number;
  strength: number;
  damping: number;
  massInverse: number;
  wind: number;
  gravity: number;
  active: boolean;
}

export class SpineManager {
  private app!: Application;
  private viewport!: Viewport;
  private artboard!: Graphics;
  private backgroundGraphics!: Graphics;
  private gridGraphics!: Graphics;
  private _artboardW = 1920;
  private _artboardH = 1080;
  private _backgroundColor: number | null = null;
  private _currentTimeScale = 1;
  private _theme: 'dark' | 'light' = 'dark';
  private spineInstances = new Map<string, Spine>();
  private imageInstances = new Map<string, Sprite>();
  private loadedAssets = new Map<string, LoadedAsset>();
  private loadedImages = new Map<string, LoadedImage>();
  private container!: HTMLDivElement;
  private vpListeners: Array<(state: ViewportState) => void> = [];
  // Promise created in constructor so waitForInit() is always safe to call before init()
  private _resolveInit!: () => void;
  private _initPromise: Promise<void> = new Promise<void>(resolve => { this._resolveInit = resolve; });
  // Dedup concurrent loads for the same asset
  private _loadingPromises = new Map<string, Promise<void>>();
  private clickListeners: Array<(layerId: string, mods: { ctrl: boolean; shift: boolean }) => void> = [];
  private emptyClickListeners: Array<() => void> = [];
  private vpDragStartListeners: Array<() => void> = [];
  private vpDragEndListeners: Array<() => void> = [];
  /** Playwright test hook — overrides getLayerScreenBounds for specific layer IDs. */
  private _testBoundsOverride = new Map<string, { x: number; y: number; width: number; height: number }>();
  /** Playwright test hook — overrides getBonePositions for specific layer IDs. */
  private _testBoneOverride = new Map<string, BonePosition[]>();
  /** Tracks the last layer right-clicked via PixiJS pointer events (consumed on read). */
  private _rightClickTarget: { layerId: string; layerType: 'spine' | 'image' } | null = null;
  /** Bound visibilitychange handler — stored so we can remove it on destroy. */
  private _visibilityHandler: (() => void) | null = null;
  // ---- Render-on-demand ----
  /** Remaining frames to render before auto-stopping the ticker when paused. */
  private _idleFrames = 0;
  // ---- Frame budget guard ----
  private _frameSamples: number[] = [];
  private _perfDegraded = false;
  private _perfListeners: Array<(status: 'ok' | 'degraded', avgMs: number) => void> = [];
  /** Asset IDs whose PNG textures could not be fetched — skeleton still loads with placeholder. */
  private _missingTextureAssets = new Set<string>();

  /** Resolves when init() completes. Always safe to call before init() starts. */
  waitForInit(): Promise<void> {
    return this._initPromise;
  }

  async init(container: HTMLDivElement): Promise<void> {
    await this._doInit(container);
    this._resolveInit();
  }

  private async _doInit(container: HTMLDivElement): Promise<void> {
    this.container = container;

    const { initPixiApp } = await import('./pixi-app');
    this.app = await initPixiApp(container);

    // Create pixi-viewport
    this.viewport = new Viewport({
      screenWidth: container.clientWidth,
      screenHeight: container.clientHeight,
      worldWidth: 5000,
      worldHeight: 5000,
      events: this.app.renderer.events,
    });

    this.app.stage.addChild(this.viewport);

    this.viewport
      .drag({ mouseButtons: 'all' })
      .pinch()
      .wheel({ smooth: 5 });

    this.viewport.on('moved', () => this._emitViewportChange());
    this.viewport.on('zoomed', () => this._emitViewportChange());

    // Emit empty-click when tapping on blank canvas area
    this.viewport.on('pointertap', () => {
      this.emptyClickListeners.forEach((cb) => cb());
      // Clear any stale right-click target when user left-taps the background
      this._rightClickTarget = null;
    });

    // Viewport drag events — used for cursor feedback
    this.viewport.on('drag-start', () => this.vpDragStartListeners.forEach((cb) => cb()));
    this.viewport.on('drag-end',   () => this.vpDragEndListeners.forEach((cb) => cb()));

    // Pause/resume the PixiJS ticker when the tab is hidden to save CPU.
    // Always restart on reveal — WebGL clears its buffer between frames so a stopped ticker
    // causes a blank canvas the moment the browser compositor touches the element.
    this._visibilityHandler = () => {
      if (!this.app) return;
      if (document.hidden) {
        this.app.ticker.stop();
      } else {
        this.app.ticker.start();
      }
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);

    // ── _idleFrames counter — kept for markDirty bookkeeping but no longer stops the ticker.
    // Stopping the ticker causes blank canvas: WebGL's preserveDrawingBuffer=false means the
    // drawing buffer is cleared between compositor frames; a stopped ticker never redraws it.
    this.app.ticker.add(() => {
      if (this._idleFrames > 0) {
        this._idleFrames--;
      }
    }, undefined, -200);

    // ── Frame-budget guard: track rolling 30-frame average; emit perf events at 30fps threshold.
    // Runs at priority -150 (before auto-stop but after normal updates).
    this.app.ticker.add(() => {
      this._frameSamples.push(this.app.ticker.deltaMS);
      if (this._frameSamples.length > 30) this._frameSamples.shift();
      if (this._frameSamples.length === 30) {
        const avg = this._frameSamples.reduce((a, b) => a + b, 0) / 30;
        const nowDegraded = avg > 33;
        if (nowDegraded !== this._perfDegraded) {
          this._perfDegraded = nowDegraded;
          this._perfListeners.forEach((cb) => cb(nowDegraded ? 'degraded' : 'ok', Math.round(avg)));
        }
      }
    }, undefined, -150);

    // Background fill (behind everything — drawn first)
    this.backgroundGraphics = new Graphics();
    this.backgroundGraphics.eventMode = 'none';
    this.viewport.addChild(this.backgroundGraphics);

    // Grid graphics (above background, below spines)
    this.gridGraphics = new Graphics();
    this.gridGraphics.visible = false;
    this.gridGraphics.eventMode = 'none';
    this.viewport.addChild(this.gridGraphics);

    // Artboard guide (drawn on top of spines, non-interactive)
    this.artboard = new Graphics();
    this.viewport.addChild(this.artboard);
  }

  destroy(): void {
    // Unload all tracked assets from PixiJS GPU/cache before clearing maps.
    for (const keys of this.loadedAssets.values()) {
      try { Assets.unload([keys.skeletonKey, keys.atlasKey]); } catch { /* ignore */ }
    }
    for (const img of this.loadedImages.values()) {
      try { Assets.unload(img.textureKey); } catch { /* ignore */ }
    }

    this.spineInstances.clear();
    this.imageInstances.clear();
    this.loadedAssets.clear();
    this.loadedImages.clear();
    this._loadingPromises.clear();
    this._testBoneOverride.clear();
    this.clickListeners = [];
    this.emptyClickListeners = [];
    this.vpDragStartListeners = [];
    this.vpDragEndListeners = [];

    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this._idleFrames = 0;
    this._frameSamples = [];
    this._perfListeners = [];
    this._missingTextureAssets.clear();
    // _initPromise stays alive — a new instance is created after destroySpineManager()
  }

  resize(width: number, height: number): void {
    if (!this.app || !this.viewport) return;
    this.app.renderer.resize(width, height);
    this.viewport.resize(width, height);
  }

  // ---- Render-on-demand ----

  /**
   * Mark the scene as needing N more frames of rendering.
   * Restarts the ticker if it was stopped (e.g. idle after pause).
   * Safe to call from any context — O(1).
   */
  markDirty(frames = 3): void {
    this._idleFrames = Math.max(this._idleFrames, frames);
    if (!document.hidden && this.app && !this.app.ticker.started) {
      this.app.ticker.start();
    }
  }

  // ---- Frame budget guard ----

  /** Subscribe to perf events. Returns an unsubscribe function. */
  onPerfChange(cb: (status: 'ok' | 'degraded', avgMs: number) => void): () => void {
    this._perfListeners.push(cb);
    return () => { this._perfListeners = this._perfListeners.filter((l) => l !== cb); };
  }

  // ---- Asset loading ----

  async loadSpineAsset(
    assetId: string,
    jsonUrl: string,
    atlasUrl: string,
    pngUrls: string[] = []
  ): Promise<void> {
    if (this.loadedAssets.has(assetId)) return;

    // If another caller is already loading this asset, wait for that promise instead
    // of starting a duplicate load (which can corrupt PixiJS asset resolution).
    const inflight = this._loadingPromises.get(assetId);
    if (inflight) return inflight;

    const promise = this._doLoadSpineAsset(assetId, jsonUrl, atlasUrl, pngUrls);
    this._loadingPromises.set(assetId, promise);
    try {
      await promise;
    } finally {
      this._loadingPromises.delete(assetId);
    }
  }

  private async _doLoadSpineAsset(
    assetId: string,
    jsonUrl: string,
    atlasUrl: string,
    pngUrls: string[],
  ): Promise<void> {
    const skeletonKey = `skeleton-${assetId}`;
    const atlasKey = `atlas-${assetId}`;
    let hasMissing = false;

    // Pre-register each PNG alias. For each PNG, do a HEAD check first.
    // If unreachable (4xx, network error), register the placeholder data URL
    // instead so the atlas still loads and the skeleton renders visibly.
    await Promise.all(pngUrls.map(async (pngUrl) => {
      try {
        const encoded = pngUrl.split('/').pop() ?? '';
        const filename = decodeURIComponent(encoded).split('/').pop() ?? '';
        if (!filename) return;

        let src = pngUrl;
        try {
          const res = await fetch(pngUrl, { method: 'HEAD' });
          if (!res.ok) {
            src = MISSING_TEXTURE_DATA_URL || pngUrl;
            hasMissing = true;
          }
        } catch {
          src = MISSING_TEXTURE_DATA_URL || pngUrl;
          hasMissing = true;
        }

        Assets.resolver.add({ alias: [filename], src });
      } catch {
        // ignore individual PNG errors
      }
    }));

    await Assets.load([
      { alias: atlasKey, src: atlasUrl },
      { alias: skeletonKey, src: jsonUrl },
    ]);

    // Crisp nearest-neighbor filtering: iterate atlas pages and override per-page texture.
    // Assets.get(atlasKey) returns a TextureAtlas (not a Texture) — access pages directly.
    const spineAtlas = Assets.get(atlasKey) as { pages?: Array<{ texture?: { texture?: Texture } }> } | undefined;
    if (spineAtlas?.pages) {
      for (const page of spineAtlas.pages) {
        const src = page?.texture?.texture?.source;
        if (src) {
          // scaleMode sets magFilter + minFilter + mipmapFilter in one call.
          // style.update() invalidates the GPU sampler cache so the new filter
          // is applied even if this texture is already uploaded to the GPU.
          src.scaleMode = 'nearest';
          src.style.update();
        }
      }
    }

    this.loadedAssets.set(assetId, { skeletonKey, atlasKey });
    if (hasMissing) this._missingTextureAssets.add(assetId);
  }

  /** Returns true if any PNG textures failed to load for this asset (skeleton renders with placeholder). */
  hasMissingTextures(assetId: string): boolean {
    return this._missingTextureAssets.has(assetId);
  }

  unloadAsset(assetId: string): void {
    const keys = this.loadedAssets.get(assetId);
    if (keys) {
      Assets.unload([keys.skeletonKey, keys.atlasKey]);
      this.loadedAssets.delete(assetId);
    }
  }

  unloadImageAsset(assetId: string): void {
    const loaded = this.loadedImages.get(assetId);
    if (loaded) {
      Assets.unload(loaded.textureKey);
      this.loadedImages.delete(assetId);
    }
  }

  // ---- Image asset loading ----

  async loadImageAsset(assetId: string, imageUrl: string): Promise<void> {
    if (this.loadedImages.has(assetId)) return;
    const key = `image-${assetId}`;
    await Assets.load({ alias: key, src: imageUrl });
    // Crisp nearest-neighbor filtering for image layers at all zoom levels
    const tex = Assets.get<Texture>(key);
    if (tex?.source) {
      tex.source.scaleMode = 'nearest';
      tex.source.style.update();
    }
    this.loadedImages.set(assetId, { textureKey: key });
  }

  addImageToScene(layerId: string, assetId: string, transform: Transform): Sprite | null {
    if (!this.viewport) return null;
    if (this.imageInstances.has(layerId)) return this.imageInstances.get(layerId)!;

    const loaded = this.loadedImages.get(assetId);
    if (!loaded) return null;

    let sprite: Sprite;
    try {
      const texture = Assets.get<Texture>(loaded.textureKey);
      if (!texture) return null;
      sprite = new Sprite(texture);
      // Center anchor so position (0,0) matches the spine origin convention
      sprite.anchor.set(0.5, 0.5);
    } catch (e) {
      console.error('[SpineManager] Failed to create sprite for', layerId, e);
      return null;
    }

    this.applyTransform(sprite as unknown as Spine, transform);

    // Insert before artboard overlay (same order as addSpineToScene)
    const artboardIdx = this.viewport.children.indexOf(this.artboard);
    this.viewport.addChildAt(sprite, artboardIdx >= 0 ? artboardIdx : this.viewport.children.length);
    this.imageInstances.set(layerId, sprite);

    // Click-to-select: intercept pointer events (same as spine)
    sprite.eventMode = 'static';
    sprite.cursor = 'pointer';
    sprite.on('pointerdown', (e: unknown) => {
      (e as { stopPropagation(): void }).stopPropagation();
      if ((e as { button: number }).button === 2) {
        this._rightClickTarget = { layerId, layerType: 'image' };
      }
    });
    sprite.on('pointertap', (e) => {
      e.stopPropagation();
      const mods = { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey };
      this.clickListeners.forEach((cb) => cb(layerId, mods));
    });
    this.markDirty();
    return sprite;
  }

  removeImageFromScene(layerId: string): void {
    const sprite = this.imageInstances.get(layerId);
    if (sprite) {
      sprite.removeAllListeners();
      this.viewport.removeChild(sprite);
      sprite.destroy();
      this.imageInstances.delete(layerId);
      this.markDirty();
    }
  }

  updateImageTransform(layerId: string, transform: Transform): void {
    const sprite = this.imageInstances.get(layerId);
    if (!sprite) return;
    this.applyTransform(sprite as unknown as Spine, transform);
    this.markDirty();
  }

  setImageVisible(layerId: string, visible: boolean): void {
    const sprite = this.imageInstances.get(layerId);
    if (sprite) { sprite.visible = visible; this.markDirty(); }
  }

  setImageOpacity(layerId: string, opacity: number): void {
    const sprite = this.imageInstances.get(layerId);
    if (sprite) { sprite.alpha = opacity; this.markDirty(); }
  }

  setImageBlendMode(layerId: string, mode: string): void {
    const sprite = this.imageInstances.get(layerId);
    if (sprite) { (sprite as unknown as { blendMode: string }).blendMode = mode; this.markDirty(); }
  }

  getImageScreenBounds(layerId: string): { x: number; y: number; width: number; height: number } | null {
    const sprite = this.imageInstances.get(layerId);
    if (!sprite) return null;
    try {
      const b = sprite.getBounds();
      return { x: b.x, y: b.y, width: b.width, height: b.height };
    } catch { return null; }
  }

  // ---- Layer management ----

  addSpineToScene(layerId: string, assetId: string, transform: Transform): Spine | null {
    if (!this.viewport) {
      console.warn('[SpineManager] addSpineToScene called before init() completed');
      return null;
    }
    if (this.spineInstances.has(layerId)) return this.spineInstances.get(layerId)!;

    const keys = this.loadedAssets.get(assetId);
    if (!keys) {
      console.error(`[SpineManager] Asset ${assetId} not found in loadedAssets`);
      return null;
    }

    let spine: Spine;
    try {
      spine = Spine.from({ skeleton: keys.skeletonKey, atlas: keys.atlasKey });
    } catch (e) {
      console.error(`[SpineManager] Spine.from failed for ${layerId}:`, e);
      return null;
    }
    this.applyTransform(spine, transform);

    // Inherit current pause/speed state so new layers don't play if scene is paused
    spine.state.timeScale = this._currentTimeScale;

    // Insert before artboard overlay
    const artboardIdx = this.viewport.children.indexOf(this.artboard);
    this.viewport.addChildAt(spine, artboardIdx);
    this.spineInstances.set(layerId, spine);

    // Click-to-select: intercept pointer events
    spine.eventMode = 'static';
    spine.cursor = 'pointer';
    spine.on('pointerdown', (e: unknown) => {
      (e as { stopPropagation(): void }).stopPropagation();
      if ((e as { button: number }).button === 2) {
        this._rightClickTarget = { layerId, layerType: 'spine' };
      }
    });
    spine.on('pointertap', (e) => {
      e.stopPropagation();
      const mods = { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey };
      this.clickListeners.forEach((cb) => cb(layerId, mods));
    });

    this.markDirty();
    return spine;
  }

  removeSpineFromScene(layerId: string): void {
    const spine = this.spineInstances.get(layerId);
    if (spine) {
      spine.removeAllListeners();
      this.viewport.removeChild(spine);
      spine.destroy();
      this.spineInstances.delete(layerId);
      this.markDirty();
    }
  }

  // ---- Animation control ----

  setAnimation(layerId: string, trackIndex: number, animName: string, loop: boolean, timeScale = 1): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    try {
      const entry = spine.state.setAnimation(trackIndex, animName, loop);
      if (entry) entry.timeScale = timeScale;
      this.markDirty();
    } catch (e) {
      console.warn(`setAnimation failed for ${layerId}:${animName}`, e);
    }
  }

  addAnimation(layerId: string, trackIndex: number, animName: string, loop: boolean, delay = 0): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    try {
      spine.state.addAnimation(trackIndex, animName, loop, delay);
    } catch (e) {
      console.warn(`addAnimation failed`, e);
    }
  }

  clearTrack(layerId: string, trackIndex: number): void {
    this.spineInstances.get(layerId)?.state.clearTrack(trackIndex);
  }

  setTrackLoop(layerId: string, trackIndex: number, loop: boolean): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const entry = spine.state.getCurrent(trackIndex);
    if (entry) entry.loop = loop;
  }

  setTrackTimeScale(layerId: string, trackIndex: number, timeScale: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const entry = spine.state.getCurrent(trackIndex);
    if (entry) entry.timeScale = timeScale;
  }

  // ---- Global playback ----

  setTimeScale(scale: number): void {
    this._currentTimeScale = scale;
    for (const spine of this.spineInstances.values()) {
      spine.state.timeScale = scale;
    }
    // When pausing (scale=0): render a few settling frames then auto-stop.
    // When playing (scale>0): markDirty restarts the ticker if it was stopped.
    this.markDirty(scale === 0 ? 4 : 3);
  }

  getAnimationDuration(layerId: string, trackIndex: number): number {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return 0;
    const entry = spine.state.getCurrent(trackIndex);
    return entry?.animation?.duration ?? 0;
  }

  /** Returns the longest active track duration across all tracks on this layer. */
  /** Return all animation names available in the loaded skeleton. */
  getAnimationNames(layerId: string): string[] {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return [];
    return spine.skeleton.data.animations.map((a) => a.name);
  }

  getMaxAnimationDuration(layerId: string): number {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return 0;
    let max = 0;
    for (let i = 0; i < 8; i++) {
      const entry = spine.state.getCurrent(i);
      if (!entry) continue;
      max = Math.max(max, entry.animation?.duration ?? 0);
    }
    return max;
  }

  getCurrentTime(layerId: string, trackIndex: number): number {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return 0;
    return spine.state.getCurrent(trackIndex)?.trackTime ?? 0;
  }

  seekTo(layerId: string, trackIndex: number, time: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const entry = spine.state.getCurrent(trackIndex);
    if (entry) entry.trackTime = time;
  }

  /** Seeks all active tracks on a layer to the given scene time (seconds). Looping tracks wrap. */
  seekAllTracks(layerId: string, time: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    for (let i = 0; i < 8; i++) {
      const entry = spine.state.getCurrent(i);
      if (!entry) continue;
      const dur = entry.animation?.duration ?? 0;
      if (dur > 0) {
        entry.trackTime = entry.loop ? time % dur : Math.min(time, dur);
      }
    }
    // Restart the ticker so the new pose is rendered.
    // When paused, the ticker auto-stops after settling frames — any manual seek must re-dirty.
    this.markDirty();
  }

  // ---- Skins ----

  setSkin(layerId: string, skinName: string): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    try {
      spine.skeleton.setSkinByName(skinName);
      spine.skeleton.setToSetupPose();
      this.markDirty();
    } catch (e) {
      console.warn(`setSkin failed`, e);
    }
  }

  // ---- Transform ----

  updateTransform(layerId: string, transform: Transform): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    this.applyTransform(spine, transform);
    this.markDirty();
  }

  private applyTransform(spine: Spine, t: Transform): void {
    spine.x = t.x;
    spine.y = t.y;
    spine.scale.set(t.scaleX, t.scaleY);
    spine.rotation = (t.rotation * Math.PI) / 180;
  }

  // ---- Bone attachment (called each frame via ticker) ----

  updateBoneAttachments(layers: Layer[]): void {
    for (const layer of layers) {
      if (!layer.attachment.parentLayerId) continue;
      const child = this.spineInstances.get(layer.id);
      if (!child) continue;

      if (layer.attachment.boneName) {
        // ── Bone attachment: follow the bone's live animated world position ──
        // bone.worldX/Y is in Spine container's local space.
        // toGlobal converts to PixiJS stage space; viewport.toLocal converts to
        // viewport world space (where child.x/y live).
        const parent = this.spineInstances.get(layer.attachment.parentLayerId);
        if (!parent) continue;
        const bone = parent.skeleton.findBone(layer.attachment.boneName);
        if (bone) {
          try {
            const globalPt = parent.toGlobal({ x: bone.worldX, y: bone.worldY });
            const localPt  = this.viewport.toLocal(globalPt);
            child.x = localPt.x;
            child.y = localPt.y;
          } catch {
            child.x = parent.x + bone.worldX;
            child.y = parent.y + bone.worldY;
          }
          child.rotation = bone.getWorldRotationX() * (Math.PI / 180);
        }
      } else {
        // ── Parent-only attachment: compose child LOCAL transform with parent's
        //    WORLD transform read directly from the store (via layersRef).
        //    Using store values — not PixiJS object state — avoids frame-sync
        //    issues where PixiJS v8's ObservablePoint may not reflect updates
        //    made in the same frame by SpineLayerRenderer.
        const pw = getLayerWorldTransform(layer.attachment.parentLayerId, layers);
        const t  = layer.transform; // LOCAL (relative to parent)

        const pr  = pw.rotation * (Math.PI / 180); // degrees → radians
        const cos = Math.cos(pr);
        const sin = Math.sin(pr);

        child.x = pw.x + cos * t.x * pw.scaleX - sin * t.y * pw.scaleY;
        child.y = pw.y + sin * t.x * pw.scaleX + cos * t.y * pw.scaleY;
        child.scale.x = pw.scaleX * t.scaleX;
        child.scale.y = pw.scaleY * t.scaleY;
        child.rotation = pr + t.rotation * (Math.PI / 180);
      }
    }
  }

  // ---- Bone world position ----

  getBoneWorldPosition(layerId: string, boneName: string): { x: number; y: number; rotation: number } {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return { x: 0, y: 0, rotation: 0 };
    const bone = spine.skeleton.findBone(boneName);
    if (!bone) return { x: 0, y: 0, rotation: 0 };
    return { x: bone.worldX, y: bone.worldY, rotation: bone.getWorldRotationX() };
  }

  /**
   * Returns all bones for a layer in PixiJS stage space (add RULER_SIZE for CSS coords).
   * Uses toGlobal() to correctly apply the Spine container's transform + viewport zoom/pan.
   * Falls back to test override when set (for Playwright tests without real Spine assets).
   */
  getBonePositions(layerId: string): BonePosition[] | null {
    if (this._testBoneOverride.has(layerId)) {
      return this._testBoneOverride.get(layerId)!;
    }
    const spine = this.spineInstances.get(layerId);
    if (!spine) return null;
    try {
      return spine.skeleton.bones.map((bone) => {
        const pt = spine.toGlobal({ x: bone.worldX, y: bone.worldY });
        return {
          name: bone.data.name,
          stageX: pt.x,
          stageY: pt.y,
          parentName: bone.parent?.data.name ?? null,
          length: bone.data.length,
        };
      });
    } catch {
      return null;
    }
  }

  // ---- IK Constraints ----

  getIkConstraints(layerId: string): IkConstraintInfo[] | null {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return null;
    try {
      return spine.skeleton.ikConstraints.map((c) => ({
        name: c.data.name,
        bones: c.bones.map((b) => b.data.name),
        targetBone: c.target.data.name,
        mix: c.mix,
        bendDirection: c.bendDirection as 1 | -1,
        softness: c.softness,
        compress: c.compress,
        stretch: c.stretch,
      }));
    } catch {
      return null;
    }
  }

  getIkTargets(layerId: string): IkTargetInfo[] | null {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return null;
    try {
      return spine.skeleton.ikConstraints.map((c) => {
        const target = c.target;
        const pt = spine.toGlobal({ x: target.worldX, y: target.worldY });
        return {
          constraintName: c.data.name,
          stageX: pt.x,
          stageY: pt.y,
          worldX: target.worldX,
          worldY: target.worldY,
          localX: target.x,
          localY: target.y,
        };
      });
    } catch {
      return null;
    }
  }

  setIkMix(layerId: string, constraintName: string, mix: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const c = spine.skeleton.ikConstraints.find((ik) => ik.data.name === constraintName);
    if (c) { c.mix = mix; spine.skeleton.updateWorldTransform(Physics.update); }
  }

  setIkBendDirection(layerId: string, constraintName: string, dir: 1 | -1): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const c = spine.skeleton.ikConstraints.find((ik) => ik.data.name === constraintName);
    if (c) { c.bendDirection = dir; spine.skeleton.updateWorldTransform(Physics.update); }
  }

  /**
   * Move an IK target bone by providing a new position in SVG/stage space.
   * Internally converts stage → skeleton world → target bone local space.
   */
  setIkTargetFromStage(layerId: string, constraintName: string, stageX: number, stageY: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const c = spine.skeleton.ikConstraints.find((ik) => ik.data.name === constraintName);
    if (!c) return;

    // Convert stage position to skeleton-local (world) coords
    const skelWorld = spine.toLocal({ x: stageX, y: stageY });
    this._setTargetBoneWorldPos(c.target, skelWorld.x, skelWorld.y);
    spine.skeleton.updateWorldTransform(Physics.update);
  }

  /** Reset a single IK target bone to its setup pose position. */
  resetIkTarget(layerId: string, constraintName: string): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const c = spine.skeleton.ikConstraints.find((ik) => ik.data.name === constraintName);
    if (!c) return;
    c.target.x = (c.target as unknown as { data: { x: number; y: number } }).data.x;
    c.target.y = (c.target as unknown as { data: { x: number; y: number } }).data.y;
    spine.skeleton.updateWorldTransform(Physics.update);
  }

  /** Apply a saved IK pose (all constraints) to the skeleton. */
  applyIkPose(layerId: string, pose: Record<string, { localX: number; localY: number; mix: number; bendDirection: 1 | -1 }>): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    for (const [name, entry] of Object.entries(pose)) {
      const c = spine.skeleton.ikConstraints.find((ik) => ik.data.name === name);
      if (!c) continue;
      c.target.x = entry.localX;
      c.target.y = entry.localY;
      c.mix = entry.mix;
      c.bendDirection = entry.bendDirection;
    }
    spine.skeleton.updateWorldTransform(Physics.update);
  }

  // ---- Physics Constraints ----

  getPhysicsConstraints(layerId: string): PhysicsConstraintInfo[] | null {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return null;
    try {
      return spine.skeleton.physicsConstraints.map((c) => ({
        name: c.data.name,
        bone: c.bone.data.name,
        mix: c.mix,
        inertia: c.inertia,
        strength: c.strength,
        damping: c.damping,
        massInverse: c.massInverse,
        wind: c.wind,
        gravity: c.gravity,
        active: c.active,
      }));
    } catch { return null; }
  }

  setPhysicsParam(
    layerId: string,
    constraintName: string,
    param: keyof Omit<PhysicsConstraintInfo, 'name' | 'bone' | 'active'>,
    value: number
  ): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const c = spine.skeleton.physicsConstraints.find((p) => p.data.name === constraintName);
    if (c) {
      (c as unknown as Record<string, number>)[param] = value;
      spine.skeleton.updateWorldTransform(Physics.update);
    }
  }

  applyPhysicsPose(layerId: string, pose: Record<string, PhysicsConstraintPose>): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    for (const [name, entry] of Object.entries(pose)) {
      const c = spine.skeleton.physicsConstraints.find((p) => p.data.name === name);
      if (!c) continue;
      c.mix = entry.mix;
      c.wind = entry.wind;
      c.gravity = entry.gravity;
      c.inertia = entry.inertia;
      c.strength = entry.strength;
      c.damping = entry.damping;
      c.massInverse = entry.massInverse;
    }
    spine.skeleton.updateWorldTransform(Physics.update);
  }

  resetPhysicsConstraint(layerId: string, constraintName: string): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    const c = spine.skeleton.physicsConstraints.find((p) => p.data.name === constraintName);
    if (c) {
      c.setToSetupPose();
      spine.skeleton.updateWorldTransform(Physics.update);
    }
  }

  /** Set wind force on ALL physics constraints across ALL loaded layers. */
  setGlobalWind(wind: number): void {
    for (const spine of this.spineInstances.values()) {
      for (const c of spine.skeleton.physicsConstraints) {
        c.wind = wind;
      }
      if (spine.skeleton.physicsConstraints.length > 0) {
        spine.skeleton.updateWorldTransform(Physics.update);
      }
    }
  }

  /** Set gravity force on ALL physics constraints across ALL loaded layers. */
  setGlobalGravity(gravity: number): void {
    for (const spine of this.spineInstances.values()) {
      for (const c of spine.skeleton.physicsConstraints) {
        c.gravity = gravity;
      }
      if (spine.skeleton.physicsConstraints.length > 0) {
        spine.skeleton.updateWorldTransform(Physics.update);
      }
    }
  }

  /** Update theme and redraw all theme-sensitive PixiJS elements. */
  setTheme(theme: 'dark' | 'light'): void {
    this._theme = theme;
    // Redraw artboard border and grid with new theme colors
    this.drawArtboard(this._artboardW, this._artboardH);
    if (this.gridGraphics?.visible) this._drawGrid();
    this.markDirty();
  }

  /** Set wind on all physics constraints for a single layer only. */
  setLayerPhysicsWind(layerId: string, wind: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    for (const c of spine.skeleton.physicsConstraints) {
      c.wind = wind;
    }
    if (spine.skeleton.physicsConstraints.length > 0) {
      spine.skeleton.updateWorldTransform(Physics.update);
    }
  }

  /** Set gravity on all physics constraints for a single layer only. */
  setLayerPhysicsGravity(layerId: string, gravity: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    for (const c of spine.skeleton.physicsConstraints) {
      c.gravity = gravity;
    }
    if (spine.skeleton.physicsConstraints.length > 0) {
      spine.skeleton.updateWorldTransform(Physics.update);
    }
  }

  /** Set a bone's local position to achieve the given skeleton world position. */
  private _setTargetBoneWorldPos(target: { x: number; y: number; parent: { worldX: number; worldY: number; a: number; b: number; c: number; d: number } | null }, worldX: number, worldY: number): void {
    const pa = target.parent;
    if (pa) {
      const dx = worldX - pa.worldX;
      const dy = worldY - pa.worldY;
      const det = pa.a * pa.d - pa.b * pa.c;
      if (Math.abs(det) > 1e-6) {
        target.x = (pa.d * dx - pa.b * dy) / det;
        target.y = (pa.a * dy - pa.c * dx) / det;
      }
    } else {
      target.x = worldX;
      target.y = worldY;
    }
  }

  /** Playwright test hook: inject fake bone positions without a real Spine asset. */
  setTestBoneOverride(layerId: string, bones: BonePosition[]): void {
    this._testBoneOverride.set(layerId, bones);
  }

  clearTestBoneOverride(): void {
    this._testBoneOverride.clear();
  }

  // ---- Viewport control ----

  setZoom(zoom: number): void {
    this.viewport?.setZoom(zoom, true);
  }

  zoomIn(): void {
    if (!this.viewport) return;
    this.viewport.setZoom(this.viewport.scale.x * 1.25, true);
    this._emitViewportChange();
  }

  zoomOut(): void {
    if (!this.viewport) return;
    this.viewport.setZoom(this.viewport.scale.x / 1.25, true);
    this._emitViewportChange();
  }

  getZoom(): number {
    return this.viewport?.scale.x ?? 1;
  }

  getViewportState(): ViewportState {
    return {
      zoom: this.viewport?.scale.x ?? 1,
      x: this.viewport?.x ?? 0,
      y: this.viewport?.y ?? 0,
    };
  }

  /** Restore a saved viewport state (for camera bookmarks). */
  setViewport(x: number, y: number, zoom: number): void {
    if (!this.viewport) return;
    this.viewport.setZoom(zoom, true);
    this.viewport.position.set(x, y);
    const state = this.getViewportState();
    this.vpListeners.forEach((cb) => cb(state));
  }

  onViewportChange(cb: (state: ViewportState) => void): () => void {
    this.vpListeners.push(cb);
    return () => { this.vpListeners = this.vpListeners.filter((l) => l !== cb); };
  }

  // ---- Click-to-select events ----

  onLayerClick(cb: (layerId: string, mods: { ctrl: boolean; shift: boolean }) => void): () => void {
    this.clickListeners.push(cb);
    return () => { this.clickListeners = this.clickListeners.filter((l) => l !== cb); };
  }

  /** Returns IDs of all visible layers whose screen AABB intersects the given stage-space rect. */
  getLayersInScreenRect(x: number, y: number, w: number, h: number): string[] {
    const results: string[] = [];
    const check = (id: string, obj: { getBounds: () => { x: number; y: number; width: number; height: number }; visible: boolean; renderable: boolean }) => {
      if (!obj.visible || !obj.renderable) return;
      try {
        const b = obj.getBounds();
        if (b.x + b.width > x && b.x < x + w && b.y + b.height > y && b.y < y + h) {
          results.push(id);
        }
      } catch { /* skip */ }
    };
    this.spineInstances.forEach((obj, id) => check(id, obj));
    this.imageInstances.forEach((obj, id) => check(id, obj));
    return results;
  }

  onEmptyClick(cb: () => void): () => void {
    this.emptyClickListeners.push(cb);
    return () => { this.emptyClickListeners = this.emptyClickListeners.filter((l) => l !== cb); };
  }

  onViewportDragStart(cb: () => void): () => void {
    this.vpDragStartListeners.push(cb);
    return () => { this.vpDragStartListeners = this.vpDragStartListeners.filter((l) => l !== cb); };
  }

  onViewportDragEnd(cb: () => void): () => void {
    this.vpDragEndListeners.push(cb);
    return () => { this.vpDragEndListeners = this.vpDragEndListeners.filter((l) => l !== cb); };
  }

  /**
   * Returns the top-most visible layer whose AABB contains the given PixiJS stage-space point.
   * Iterates in reverse insertion order so layers added later (rendered on top) are checked first.
   */
  getLayerAtPoint(stageX: number, stageY: number): { layerId: string; layerType: 'spine' | 'image' } | null {
    const combined = [
      ...[...this.spineInstances.keys()].map((id) => ({ id, type: 'spine' as const })),
      ...[...this.imageInstances.keys()].map((id) => ({ id, type: 'image' as const })),
    ].reverse();

    for (const { id, type } of combined) {
      const obj = type === 'spine' ? this.spineInstances.get(id) : this.imageInstances.get(id);
      if (!obj || !obj.visible) continue;
      try {
        const b = obj.getBounds();
        if (stageX >= b.x && stageX <= b.x + b.width && stageY >= b.y && stageY <= b.y + b.height) {
          return { layerId: id, layerType: type };
        }
      } catch { /* skip */ }
    }
    return null;
  }

  /**
   * Returns (and clears) the last layer hit by a right-click pointer event.
   * Returns null if the background was right-clicked.
   */
  getLastRightClick(): { layerId: string; layerType: 'spine' | 'image' } | null {
    const t = this._rightClickTarget;
    this._rightClickTarget = null;
    return t;
  }

  /** Returns bounds in PixiJS screen (stage) space — add RULER_SIZE for container-relative coords. */
  getLayerScreenBounds(layerId: string): { x: number; y: number; width: number; height: number } | null {
    if (this._testBoundsOverride.has(layerId)) return this._testBoundsOverride.get(layerId)!;
    const spine = this.spineInstances.get(layerId);
    if (spine) {
      try {
        const b = spine.getBounds();
        return { x: b.x, y: b.y, width: b.width, height: b.height };
      } catch {
        return null;
      }
    }
    const sprite = this.imageInstances.get(layerId);
    if (sprite) {
      try {
        const b = sprite.getBounds();
        return { x: b.x, y: b.y, width: b.width, height: b.height };
      } catch {
        return null;
      }
    }
    return null;
  }

  /** Returns the live PixiJS state of a spine instance — used by Playwright tests to verify rendering. */
  getSpinePixiState(layerId: string): { x: number; y: number; scaleX: number; scaleY: number; rotation: number } | null {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return null;
    return { x: spine.x, y: spine.y, scaleX: spine.scale.x, scaleY: spine.scale.y, rotation: spine.rotation };
  }

  /**
   * Playwright test hook: register a lightweight mock object as a spine instance.
   * Allows ticker tests without real Spine assets — the ticker reads .x/.y/.scale/.rotation
   * and writes to them, so any object with those properties works.
   */
  setTestSpineInstance(layerId: string, mockSpine: {
    x: number; y: number;
    scale: { x: number; y: number };
    rotation: number;
    getBounds?: () => { x: number; y: number; width: number; height: number };
    renderable?: boolean;
  }): void {
    this.spineInstances.set(layerId, mockSpine as unknown as Spine);
  }

  /** Playwright test hook: read the renderable flag for a layer (spine or image). */
  getLayerRenderable(layerId: string): boolean | null {
    const obj = this.spineInstances.get(layerId) ?? this.imageInstances.get(layerId);
    if (!obj) return null;
    return (obj as unknown as { renderable: boolean }).renderable;
  }

  clearTestSpineInstances(): void {
    this.spineInstances.clear();
  }

  /** Playwright test hook: inject fake bounds without needing a real Spine asset. */
  setTestBoundsOverride(layerId: string, bounds: { x: number; y: number; width: number; height: number }): void {
    this._testBoundsOverride.set(layerId, bounds);
  }

  clearTestBoundsOverride(): void {
    this._testBoundsOverride.clear();
  }

  pauseViewportDrag(): void {
    try { this.viewport?.plugins.pause('drag'); } catch { /* ignore */ }
  }

  resumeViewportDrag(): void {
    try { this.viewport?.plugins.resume('drag'); } catch { /* ignore */ }
  }

  /**
   * In pan mode all layer objects must be non-interactive so pointer events
   * fall through to the viewport drag plugin instead of being eaten by sprites.
   */
  setPanMode(enabled: boolean): void {
    const mode = enabled ? 'none' : 'static';
    for (const spine of this.spineInstances.values()) {
      spine.eventMode = mode;
    }
    for (const sprite of this.imageInstances.values()) {
      sprite.eventMode = mode;
    }
  }

  panTo(x: number, y: number): void {
    this.viewport?.moveCenter(x, y);
  }

  private _emitViewportChange(): void {
    if (!this.viewport) return;
    const state: ViewportState = {
      zoom: this.viewport.scale.x,
      x: this.viewport.x,
      y: this.viewport.y,
    };
    this.vpListeners.forEach((cb) => cb(state));
    this.updateVisibilityCulling();
    this.markDirty();
  }

  focusLayer(layerId: string): void {
    if (!this.viewport || !this.container) return;
    const obj = this.spineInstances.get(layerId) ?? this.imageInstances.get(layerId);
    if (!obj) return;
    try {
      // getBounds() returns screen-space (global pixel) coordinates.
      // viewport.moveCenter() expects world-space coordinates.
      // Conversion: world = (screen - viewport.position) / zoom
      const screenBounds = obj.getBounds();
      const currentZoom = this.viewport.scale.x;

      // World-space dimensions of the bounding box
      const worldW = Math.max(screenBounds.width / currentZoom, 100);
      const worldH = Math.max(screenBounds.height / currentZoom, 100);

      // Target zoom: fit the layer in 70% of the viewport
      const targetZoom = Math.min(
        (this.container.clientWidth / worldW) * 0.7,
        (this.container.clientHeight / worldH) * 0.7,
      );

      // World-space center of the bounding box
      const screenCx = screenBounds.x + screenBounds.width / 2;
      const screenCy = screenBounds.y + screenBounds.height / 2;
      const worldCx = (screenCx - this.viewport.x) / currentZoom;
      const worldCy = (screenCy - this.viewport.y) / currentZoom;

      this.viewport.setZoom(targetZoom, true);
      this.viewport.moveCenter(worldCx, worldCy);
      this._emitViewportChange();
    } catch {
      // ignore bounds errors
    }
  }

  fitToArtboard(canvasWidth: number, canvasHeight: number): void {
    if (!this.viewport || !this.container) return;
    const scaleX = this.container.clientWidth / canvasWidth;
    const scaleY = this.container.clientHeight / canvasHeight;
    const scale = Math.min(scaleX, scaleY) * 0.9;
    this.viewport.setZoom(scale, true);
    this.viewport.moveCenter(0, 0); // artboard is centered at world origin
    this._emitViewportChange();
  }

  // ---- Background ----

  setBackground(hex: string | null): void {
    this._backgroundColor = hex ? parseInt(hex.replace('#', ''), 16) : null;
    this._drawBackground();
    this.markDirty();
  }

  private _drawBackground(): void {
    if (!this.backgroundGraphics) return;
    this.backgroundGraphics.clear();
    if (this._backgroundColor !== null) {
      this.backgroundGraphics
        .rect(-this._artboardW / 2, -this._artboardH / 2, this._artboardW, this._artboardH)
        .fill({ color: this._backgroundColor, alpha: 1 });
    }
  }

  // ---- Artboard guide ----

  drawArtboard(width: number, height: number): void {
    if (!this.artboard) return;
    this._artboardW = width;
    this._artboardH = height;
    this._drawBackground();
    this.artboard.clear();
    // Artboard is centered at world origin so that position (0,0) = center of canvas
    const artboardBorder = this._theme === 'light' ? 0xa8a59c : 0x6a6a8a;
    this.artboard
      .rect(-width / 2, -height / 2, width, height)
      .fill({ color: 0x000000, alpha: 0 })
      .stroke({ color: artboardBorder, width: 3, alpha: 0.9 });
    this.artboard.eventMode = 'none';
    // Redraw grid if visible so it matches new artboard size
    if (this.gridGraphics?.visible) this._drawGrid();
  }

  setGridVisible(visible: boolean): void {
    if (!this.gridGraphics) return;
    this.gridGraphics.visible = visible;
    if (visible) this._drawGrid();
  }

  private _drawGrid(): void {
    if (!this.gridGraphics) return;
    this.gridGraphics.clear();
    const w = this._artboardW;
    const h = this._artboardH;
    const step = 100; // world units per grid cell
    const left = -w / 2;
    const right = w / 2;
    const top = -h / 2;
    const bottom = h / 2;
    // Major lines every 100 units
    for (let x = Math.ceil(left / step) * step; x <= right; x += step) {
      this.gridGraphics.moveTo(x, top).lineTo(x, bottom);
    }
    for (let y = Math.ceil(top / step) * step; y <= bottom; y += step) {
      this.gridGraphics.moveTo(left, y).lineTo(right, y);
    }
    const gridColor = this._theme === 'light' ? 0xb8b5ac : 0x3a3a52;
    this.gridGraphics.stroke({ color: gridColor, width: 1, alpha: 0.7 });
  }

  // ---- Visibility culling ----

  /**
   * Sets spine.renderable based on whether the layer intersects the visible viewport.
   * Uses `renderable` (not timeScale) so animation state stays in sync —
   * avoids glitches when panning back to an off-screen layer.
   * Called from the viewport-change handler (after pan/zoom).
   */
  updateVisibilityCulling(): void {
    if (!this.viewport || !this.container) return;
    // getBounds() returns screen-space (global pixel) coordinates.
    // Compare against the screen container dimensions [0..cw] x [0..ch],
    // NOT against getVisibleBounds() which is in world-space.
    const cw = this.container.clientWidth;
    const ch = this.container.clientHeight;

    for (const spine of this.spineInstances.values()) {
      const b = spine.getBounds();
      const onScreen = b.x + b.width > 0 && b.x < cw && b.y + b.height > 0 && b.y < ch;
      spine.renderable = onScreen;
    }
    // Image layers use the same culling to avoid overdraw
    for (const sprite of this.imageInstances.values()) {
      try {
        const b = sprite.getBounds();
        const onScreen = b.x + b.width > 0 && b.x < cw && b.y + b.height > 0 && b.y < ch;
        sprite.renderable = onScreen;
      } catch {
        // getBounds can throw before texture is ready — leave renderable as-is
      }
    }
  }

  // ---- Onion Skinning ----

  /**
   * Capture ghost frames for onion skinning.
   * Seeks the selected layer to offset times, renders to internal WebGL buffer,
   * copies to offscreen canvases, then restores current time.
   * Returns before-frames (warm tint) and after-frames (cool tint) as data URLs.
   * Safe to call synchronously — browser paints only after JS task completes.
   */
  captureOnionFrames(
    layerId: string,
    currentTime: number,
    offsets: number[], // positive = after, negative = before
  ): Array<{ dataUrl: string; type: 'before' | 'after' }> {
    if (!this.app || !this.viewport) return [];
    const spine = this.spineInstances.get(layerId);
    if (!spine) return [];

    const results: Array<{ dataUrl: string; type: 'before' | 'after' }> = [];
    const zoom = this.viewport.scale.x;
    const stageLeft = this.viewport.x - (this._artboardW / 2) * zoom;
    const stageTop  = this.viewport.y - (this._artboardH / 2) * zoom;
    const stageW    = this._artboardW * zoom;
    const stageH    = this._artboardH * zoom;
    const dpr = window.devicePixelRatio || 1;
    const srcX = Math.round(stageLeft * dpr);
    const srcY = Math.round(stageTop  * dpr);
    const srcW = Math.round(stageW    * dpr);
    const srcH = Math.round(stageH    * dpr);

    if (srcW <= 0 || srcH <= 0) return [];

    const dest = document.createElement('canvas');
    dest.width  = Math.max(1, Math.round(stageW));
    dest.height = Math.max(1, Math.round(stageH));

    for (const offset of offsets) {
      const ghostTime = currentTime + offset;
      if (ghostTime < 0) continue;
      try {
        this.seekAllTracks(layerId, ghostTime);
        this.app.renderer.render(this.app.stage);

        const ctx = dest.getContext('2d');
        if (!ctx) continue;
        ctx.clearRect(0, 0, dest.width, dest.height);
        ctx.drawImage(
          this.app.canvas as HTMLCanvasElement,
          srcX, srcY, srcW, srcH,
          0, 0, dest.width, dest.height
        );
        results.push({ dataUrl: dest.toDataURL('image/png'), type: offset < 0 ? 'before' : 'after' });
      } catch {
        // Ignore individual ghost capture errors
      }
    }

    // Restore
    try {
      this.seekAllTracks(layerId, currentTime);
      this.app.renderer.render(this.app.stage);
    } catch { /* ignore */ }

    return results;
  }

  // ---- Screenshot / Thumbnails ----

  /** Capture a 64×64 thumbnail of a single layer by cropping the PixiJS canvas. */
  captureLayerThumbnail(layerId: string): string | null {
    const bounds = this.getLayerScreenBounds(layerId);
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;

    try {
      const canvas = this.app.canvas as HTMLCanvasElement;
      const { x, y, width, height } = bounds;
      const SIZE = 64;

      const off = document.createElement('canvas');
      off.width = SIZE;
      off.height = SIZE;
      const ctx = off.getContext('2d');
      if (!ctx) return null;

      ctx.fillStyle = '#1a1a22';
      ctx.fillRect(0, 0, SIZE, SIZE);

      const dpr = window.devicePixelRatio || 1;
      // canvas physical coords = css coords × dpr
      const scale = Math.min(SIZE / width, SIZE / height) * 0.85;
      const sw = width * scale;
      const sh = height * scale;
      const dx = (SIZE - sw) / 2;
      const dy = (SIZE - sh) / 2;

      ctx.drawImage(canvas, x * dpr, y * dpr, width * dpr, height * dpr, dx, dy, sw, sh);
      return off.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  async captureScreenshot(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const canvas = this.app.canvas as HTMLCanvasElement;
        const dpr  = window.devicePixelRatio || 1;
        const zoom = this.viewport.scale.x;

        // Artboard is centered at world origin; compute its rect in stage (CSS) space
        const stageLeft = this.viewport.x - (this._artboardW / 2) * zoom;
        const stageTop  = this.viewport.y - (this._artboardH / 2) * zoom;
        const stageW    = this._artboardW * zoom;
        const stageH    = this._artboardH * zoom;

        // Convert to physical canvas pixels
        const srcX = Math.round(stageLeft * dpr);
        const srcY = Math.round(stageTop  * dpr);
        const srcW = Math.round(stageW    * dpr);
        const srcH = Math.round(stageH    * dpr);

        // Render at native artboard resolution
        const off = document.createElement('canvas');
        off.width  = this._artboardW;
        off.height = this._artboardH;
        const ctx = off.getContext('2d');
        if (!ctx) return reject(new Error('No 2D context'));

        // Fill background colour if set
        if (this._backgroundColor !== null) {
          ctx.fillStyle = `#${this._backgroundColor.toString(16).padStart(6, '0')}`;
          ctx.fillRect(0, 0, this._artboardW, this._artboardH);
        }

        ctx.drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, this._artboardW, this._artboardH);

        off.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Screenshot failed'));
        }, 'image/png');
      } catch (e) {
        reject(e);
      }
    });
  }

  // ---- Animation Export helpers ----

  getArtboardDimensions(): { width: number; height: number } {
    return { width: this._artboardW, height: this._artboardH };
  }

  /** Max animation duration (seconds) across all active Spine layers. */
  getSceneDuration(): number {
    let max = 0;
    for (const layerId of this.spineInstances.keys()) {
      max = Math.max(max, this.getMaxAnimationDuration(layerId));
    }
    return max || 3; // fallback 3s for image-only scenes
  }

  /** Max animation duration across all Spine layers, returns 0 if no spine layers exist. */
  getMaxSpineDuration(): number {
    let max = 0;
    for (const layerId of this.spineInstances.keys()) {
      max = Math.max(max, this.getMaxAnimationDuration(layerId));
    }
    return max;
  }

  /** Seek ALL Spine layers to the given time simultaneously. */
  seekAllLayersToTime(time: number): void {
    for (const layerId of this.spineInstances.keys()) {
      this.seekAllTracks(layerId, time);
    }
  }

  /**
   * Force-render the PixiJS stage and capture the artboard crop.
   * Returns an offscreen canvas element at artboard resolution.
   * Caller must NOT cache the returned canvas — it is reused across calls.
   */
  captureArtboardRawCanvas(outCanvas: HTMLCanvasElement): boolean {
    if (!this.app || !this.viewport) return false;
    try {
      this.app.renderer.render(this.app.stage);
      const src = this.app.canvas as HTMLCanvasElement;
      const dpr = window.devicePixelRatio || 1;
      const zoom = this.viewport.scale.x;
      const stageLeft = this.viewport.x - (this._artboardW / 2) * zoom;
      const stageTop  = this.viewport.y - (this._artboardH / 2) * zoom;
      const srcX = Math.round(stageLeft * dpr);
      const srcY = Math.round(stageTop  * dpr);
      const srcW = Math.round(this._artboardW * zoom * dpr);
      const srcH = Math.round(this._artboardH * zoom * dpr);
      if (srcW <= 0 || srcH <= 0) return false;
      const ctx = outCanvas.getContext('2d');
      if (!ctx) return false;
      if (this._backgroundColor !== null) {
        ctx.fillStyle = `#${this._backgroundColor.toString(16).padStart(6, '0')}`;
        ctx.fillRect(0, 0, outCanvas.width, outCanvas.height);
      } else {
        ctx.clearRect(0, 0, outCanvas.width, outCanvas.height);
      }
      ctx.drawImage(src, srcX, srcY, srcW, srcH, 0, 0, outCanvas.width, outCanvas.height);
      return true;
    } catch {
      return false;
    }
  }

  getPixiCanvas(): HTMLCanvasElement | null {
    return this.app ? (this.app.canvas as HTMLCanvasElement) : null;
  }

  // ---- Layer z-order ----

  reorderLayers(layerIds: string[]): void {
    // Move each child to its target index only if it has changed (avoids unnecessary splice ops).
    if (!this.viewport) return;
    const artboardIdx = this.viewport.children.indexOf(this.artboard);
    const maxIdx = artboardIdx > 0 ? artboardIdx - 1 : 0;
    for (let i = 0; i < layerIds.length; i++) {
      const child = this.spineInstances.get(layerIds[i]) ?? this.imageInstances.get(layerIds[i]);
      if (!child) continue;
      const targetIdx = Math.min(i, maxIdx);
      if (this.viewport.getChildIndex(child) !== targetIdx) {
        this.viewport.setChildIndex(child, targetIdx);
      }
    }
    this.markDirty();
  }

  // ---- Visibility ----

  setLayerVisible(layerId: string, visible: boolean): void {
    const spine = this.spineInstances.get(layerId);
    if (spine) { spine.visible = visible; this.markDirty(); return; }
    const sprite = this.imageInstances.get(layerId);
    if (sprite) { sprite.visible = visible; this.markDirty(); }
  }

  // ---- Opacity ----

  setLayerOpacity(layerId: string, opacity: number): void {
    const spine = this.spineInstances.get(layerId);
    if (spine) { spine.alpha = opacity; this.markDirty(); return; }
    const sprite = this.imageInstances.get(layerId);
    if (sprite) { sprite.alpha = opacity; this.markDirty(); }
  }

  // ---- Blend mode ----

  setLayerBlendMode(layerId: string, mode: string): void {
    const spine = this.spineInstances.get(layerId);
    if (spine) { (spine as unknown as { blendMode: string }).blendMode = mode; this.markDirty(); return; }
    const sprite = this.imageInstances.get(layerId);
    if (sprite) { (sprite as unknown as { blendMode: string }).blendMode = mode; this.markDirty(); }
  }

  // ---- Color Effects (ColorMatrixFilter) ----

  setLayerEffects(layerId: string, brightness: number, saturation: number, tint: string | null): void {
    const target = (this.spineInstances.get(layerId) ?? this.imageInstances.get(layerId)) as
      | { filters: unknown[] | null }
      | undefined;
    if (!target) return;

    const isNoop = brightness === 1 && saturation === 0 && tint === null;
    if (isNoop) {
      target.filters = null;
      this.markDirty();
      return;
    }

    const cmf = new ColorMatrixFilter();
    cmf.brightness(brightness, false);
    cmf.saturate(saturation, false);
    if (tint) {
      const r = parseInt(tint.slice(1, 3), 16) / 255;
      const g = parseInt(tint.slice(3, 5), 16) / 255;
      const b = parseInt(tint.slice(5, 7), 16) / 255;
      cmf.tint([r, g, b], false);
    }
    target.filters = [cmf];
    this.markDirty();
  }

  // ---- Crossfade ----

  /** Set the default crossfade mix duration for all transitions on a layer. */
  setDefaultMix(layerId: string, duration: number): void {
    const spine = this.spineInstances.get(layerId);
    if (spine) spine.state.data.defaultMix = duration;
  }

  /** Set crossfade mix duration between two specific animations on a layer. */
  setMixDuration(layerId: string, fromAnim: string, toAnim: string, duration: number): void {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return;
    try {
      spine.state.data.setMix(fromAnim, toAnim, duration);
    } catch {
      // Ignore — animation names may not exist yet
    }
  }

  // ---- Keyframe times (dope sheet) ----

  /**
   * Returns all unique keyframe times (in seconds) across all timelines in a given animation track.
   * Used to render dope-sheet diamond markers in the timeline panel.
   *
   * Spine timeline types store keyframe data differently:
   * - EventTimeline: frames[] = pure times, one per event
   * - RotateTimeline / TranslateTimeline / etc: frames[] interleaves [time, value, time, value…]
   *   with getFrameEntries() returning the number of floats per keyframe (usually 2 or 3).
   * We stride by getFrameEntries() when available, else assume pure-time arrays (EventTimeline).
   */
  getKeyframeTimes(layerId: string, trackIndex = 0): number[] {
    const spine = this.spineInstances.get(layerId);
    // Guard against test mocks that don't have a real Spine state object
    const spineAny = spine as unknown as { state?: { getCurrent?: unknown } };
    if (!spine || typeof spineAny.state?.getCurrent !== 'function') return [];
    const entry = spine.state.getCurrent(trackIndex);
    if (!entry?.animation) return [];

    const times = new Set<number>();
    for (const timeline of entry.animation.timelines) {
      const tl = timeline as unknown as {
        frames?: number[];
        getFrameEntries?: () => number;
      };
      if (!Array.isArray(tl.frames) || tl.frames.length === 0) continue;
      // Stride: getFrameEntries() tells us how many floats per keyframe.
      // For EventTimeline it returns 1 (time only). For CurveTimeline2 it returns 6.
      // Striding by this value always gives us the time fields at index 0, stride, 2*stride, ...
      const stride = tl.getFrameEntries?.() ?? 1;
      for (let i = 0; i < tl.frames.length; i += stride) {
        const t = tl.frames[i];
        if (typeof t === 'number' && Number.isFinite(t) && t >= 0) {
          times.add(parseFloat(t.toFixed(4)));
        }
      }
    }
    return [...times].sort((a, b) => a - b);
  }

  // ---- Event markers ----

  /** Returns all event keyframes for a given animation track (time in seconds, name from eventData). */
  getEventMarkers(layerId: string, trackIndex = 0): Array<{ time: number; name: string }> {
    const spine = this.spineInstances.get(layerId);
    if (!spine) return [];
    const entry = spine.state.getCurrent(trackIndex);
    if (!entry?.animation) return [];

    const markers: Array<{ time: number; name: string }> = [];
    for (const timeline of entry.animation.timelines) {
      // Duck-type check: EventTimeline has .events array and .frames array
      const tl = timeline as unknown as { frames?: number[]; events?: Array<{ data?: { name?: string } }> };
      if (!Array.isArray(tl.events) || !Array.isArray(tl.frames)) continue;
      for (let i = 0; i < tl.frames.length; i++) {
        markers.push({
          time: tl.frames[i],
          name: tl.events[i]?.data?.name ?? 'event',
        });
      }
    }
    return markers;
  }

  get viewportInstance(): Viewport {
    return this.viewport;
  }
}

// Singleton per editor session
let managerInstance: SpineManager | null = null;

export function getSpineManager(): SpineManager {
  if (!managerInstance) managerInstance = new SpineManager();
  return managerInstance;
}

export function destroySpineManager(): void {
  if (managerInstance) {
    managerInstance.destroy();
    managerInstance = null;
  }
}
